import memoizeOne from 'memoize-one';
import { accuracyTagsGroup, languageTagsGroup, problemTagsGroup } from './tags';
const SUPPORTED_FILTER_TYPES = ['string', 'enum', 'number', 'boolean', 'date'];
export const BUILT_IN_FILTERS_BY_PARAM = {
  type: {
    label: 'Conversation type',
    param: 'type',
    type: 'enum',
    options: [{
      label: 'Test',
      value: 'test'
    }, {
      label: 'Real',
      value: 'real'
    }, {
      label: 'Imported',
      value: 'imported'
    }]
  },
  reviewStatus: {
    label: 'Conversation',
    param: 'reviewStatus',
    type: 'enum',
    options: [{
      label: 'Good conversation',
      value: 'good'
    }, {
      label: 'Bad conversation',
      value: 'bad'
    }, {
      label: 'Not review',
      value: 'not-reviewed'
    }]
  },
  messageReviewStatus: {
    label: 'Response',
    param: 'messageReviewStatus',
    type: 'enum',
    options: [{
      label: 'Reviewed',
      value: 'reviewed'
    }, {
      label: 'Not reviewed',
      value: 'not-reviewed'
    }]
  },
  lastUpdated: {
    label: 'Last updated',
    param: 'lastUpdated',
    type: 'date'
  },
  feedback: {
    label: 'Customer feedback',
    param: 'feedback',
    type: 'enum',
    options: [{
      label: 'Good response',
      value: 'good'
    }, {
      label: 'Bad response',
      value: 'bad'
    }]
  },
  tags: {
    label: 'Review tags',
    param: 'tags',
    type: 'enum',
    options: [accuracyTagsGroup, languageTagsGroup, problemTagsGroup]
  }
};
export const BUILT_IN_FILTERS = Object.values(BUILT_IN_FILTERS_BY_PARAM);
export const BUILT_IN_FILTERS_MAP = new Map(BUILT_IN_FILTERS.map(filter => [filter.param, filter]));
export const isValidDateQueryValue = value => {
  if (typeof value === 'string') {
    return ['today', 'yesterday', 'past7Days', 'past30Days'].includes(value);
  }
  if (typeof value === 'object' && value) {
    if ('to' in value && 'from' in value) {
      return ['string', 'undefined'].includes(typeof value.to) && ['string', 'undefined'].includes(typeof value.from);
    }
    if ('to' in value) {
      return ['string', 'undefined'].includes(typeof value.to);
    }
    if ('from' in value) {
      return ['string', 'undefined'].includes(typeof value.from);
    }
  }
  return false;
};
const BUILT_IN_FILTER_QUERY_PARAMS = BUILT_IN_FILTERS.map(({
  param
}) => param);
export const isBuiltInFilterQueryParam = param => {
  return BUILT_IN_FILTER_QUERY_PARAMS.some(x => x === param);
};
export const isFilterField = param => {
  return isBuiltInFilterQueryParam(param);
};
const QUERY_STRING_PARSERS = {
  string: value => value,
  number: value => isNaN(Number(value)) ? undefined : Number(value),
  boolean: value => ['true', 'false'].some(v => v === value) ? value : undefined,
  enum: value => value.split(','),
  date: value => {
    const betweenMatch = value.match(/^between\.(\d{4}-\d{2}-\d{2}(?:T.*Z)?)?\.(\d{4}-\d{2}-\d{2}(?:T.*Z)?)?$/);
    if (betweenMatch) {
      const [, a, b] = betweenMatch !== null && betweenMatch !== void 0 ? betweenMatch : [];
      const from = Boolean(a) ? a : undefined;
      const to = Boolean(b) ? b : undefined;
      return {
        from,
        to
      };
    }
    const fromToMatch = value.match(/^(from|to)\.(\d{4}-\d{2}-\d{2}(?:T.*Z)?)$/);
    if (fromToMatch) {
      const [, operation, date] = fromToMatch !== null && fromToMatch !== void 0 ? fromToMatch : [];
      return operation === 'from' ? {
        from: date
      } : {
        to: date
      };
    }
    if (isValidDateQueryValue(value)) {
      return value;
    }
  }
};
const startOfDate = date => {
  const newDate = new Date(date);
  newDate.setHours(0, 0, 0, 0);
  return newDate;
};
const normaliseDateString = (date, endOfDate) => {
  const isDateOnlyString = /^\d{4}-\d{2}-\d{2}$/.test(date);
  if (!isDateOnlyString) {
    return new Date(date);
  }
  const d = new Date(date);
  if (!endOfDate) {
    d.setHours(0, 0, 0, 0);
  } else {
    d.setHours(23, 59, 59, 999);
  }
  return d;
};
export const BUILT_IN_FILTERS_RESOURCE_QUERY_SERIALISERS = {
  type: value => [['conversationType', String(value)]],
  lastUpdated: value => {
    if (typeof value === 'string') {
      switch (value) {
        case 'today':
          return [['updatedAfter', startOfDate(new Date()).toISOString()]];
        case 'yesterday':
          return [['updatedAfter', startOfDate(new Date(new Date().setDate(new Date().getDate() - 1))).toISOString()], ['updatedBefore', startOfDate(new Date(new Date().setDate(new Date().getDate() - 0))).toISOString()]];
        case 'past7Days':
          return [['updatedAfter', startOfDate(new Date(new Date().setDate(new Date().getDate() - 7))).toISOString()]];
        case 'past30Days':
          return [['updatedAfter', startOfDate(new Date(new Date().setDate(new Date().getDate() - 30))).toISOString()]];
      }
    }
    if (typeof value === 'object') {
      if ('from' in value && 'to' in value && value.from && value.to) {
        return [['updatedAfter', normaliseDateString(value.from).toISOString()], ['updatedBefore', normaliseDateString(value.to, true).toISOString()]];
      }
      if ('from' in value && value.from) {
        return [['updatedAfter', normaliseDateString(value.from).toISOString()]];
      }
      if ('to' in value && value.to) {
        return [['updatedBefore', normaliseDateString(value.to).toISOString()]];
      }
    }
    return [];
  },
  feedback: value => [['customerFeedback', String(value)]],
  reviewStatus: value => [['conversationReviewStatus', String(value)]],
  messageReviewStatus: value => [['messageReviewStatus', String(value)]],
  tags: value => [['annotatedLabels', String(value)]]
};

// v1 updatedBefore, v2 lastMessageTimestampBefore
// v1 updatedAfter, v2 lastMessageTimestampAfter
export const BUILT_IN_FILTERS_RESOURCE_QUERY_DESERIALISERS = {
  ...BUILT_IN_FILTERS_RESOURCE_QUERY_SERIALISERS,
  lastUpdated: value => {
    if (typeof value === 'string') {
      switch (value) {
        case 'today':
          return [['lastMessageTimestampAfter', startOfDate(new Date()).toISOString()]];
        case 'yesterday':
          return [['lastMessageTimestampAfter', startOfDate(new Date(new Date().setDate(new Date().getDate() - 1))).toISOString()], ['lastMessageTimestampBefore', startOfDate(new Date(new Date().setDate(new Date().getDate() - 0))).toISOString()]];
        case 'past7Days':
          return [['lastMessageTimestampAfter', startOfDate(new Date(new Date().setDate(new Date().getDate() - 7))).toISOString()]];
        case 'past30Days':
          return [['lastMessageTimestampAfter', startOfDate(new Date(new Date().setDate(new Date().getDate() - 30))).toISOString()]];
      }
    }
    if (typeof value === 'object') {
      if ('from' in value && 'to' in value && value.from && value.to) {
        return [['lastMessageTimestampAfter', normaliseDateString(value.from).toISOString()], ['lastMessageTimestampBefore', normaliseDateString(value.to, true).toISOString()]];
      }
      if ('from' in value && value.from) {
        return [['lastMessageTimestampAfter', normaliseDateString(value.from).toISOString()]];
      }
      if ('to' in value && value.to) {
        return [['lastMessageTimestampBefore', normaliseDateString(value.to).toISOString()]];
      }
    }
    return [];
  }
};

/**
 * Given a parsed/normalised filters query, serialise it to a URLSearchParams object
 * to persist the state in the URL
 */
export const serialiseFiltersQueryToSearchParams = memoizeOne(query => {
  return Object.entries(query).reduce((searchParams, [param, value]) => {
    const filters = BUILT_IN_FILTERS_MAP;
    const filter = filters.get(param);
    if (!filter || typeof value === 'undefined') {
      return searchParams;
    }
    const serialisers = BUILT_IN_FILTERS_RESOURCE_QUERY_DESERIALISERS;
    const serialiser = serialisers[filter.param];
    if (filter.param in serialisers) {
      const params = serialiser(value);
      params.forEach(([key, value]) => {
        searchParams.append(key, value);
      });
      return searchParams;
    }
    return searchParams;
  }, new URLSearchParams());
});

/**
 * Given raw query parameters returned from the router, parse them into a
 * normalised set of filters/query for only the supported filters
 */
export const getFiltersQuery = memoizeOne(query => {
  const filters = BUILT_IN_FILTERS_MAP;
  return Object.entries(query).reduce((acc, [param, value]) => {
    if (!isFilterField(param)) {
      return acc;
    }
    const filter = filters.get(param);
    if (!filter) {
      return acc;
    }
    try {
      const parsedValue = QUERY_STRING_PARSERS[filter.type](value);
      return {
        ...acc,
        [param]: parsedValue
      };
    } catch (err) {
      return acc;
    }
  }, {});
});